import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiSave, HiX } from "react-icons/hi";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

interface FaqCategoryFormData {
  name: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
}

interface FaqCategoryFormErrors {
  name?: string;
}

export const CreateFaqCategory: React.FC = () => {
  const { t, i18n } = useTranslation(["faqCategory", "common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [formData, setFormData] = useState<FaqCategoryFormData>({
    name: "",
    description: "",
    displayOrder: 0,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FaqCategoryFormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FaqCategoryFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("faqCategory:validation.nameRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        description: formData.description.trim() ? formData.description : null
      };

      const response = await adminApi.call("/faqcategory", "POST", payload);

      if (!response.success) {
        showErrorToast(response.error || t("faqCategory:messages.createError"));
        return;
      }

      showSuccessToast(t("faqCategory:messages.createSuccess"));
      setTimeout(() => {
        navigate("/faq-categories");
      }, 1500);
    } catch (error) {
      showErrorToast(t("faqCategory:messages.createError"));
      console.error("Error creating FAQ category:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t("faqCategory:createFaqCategory")}
      subtitle={t("faqCategory:subtitle")}
      breadcrumbs={[
        { label: t("faqCategory:breadcrumbs.faqCategories"), path: "/faq-categories" },
        { label: t("faqCategory:breadcrumbs.create") }
      ]}
      className={isRTL ? "rtl" : "ltr"}
      headerActions={
        <div className="header-actions">
          <ActionButton variant="secondary" icon={HiX} onClick={() => navigate("/faq-categories")}> 
            {t("common:cancel")}
          </ActionButton>
          <ActionButton variant="primary" icon={HiSave} onClick={handleSubmit} loading={loading} disabled={loading}>
            {t("common:save")}
          </ActionButton>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="modern-form">
        <div className="form-grid">
          <div className="form-group full-width">
            <MultiLanguageTextInput
              label={t("faqCategory:fields.name")}
              value={formData.name}
              onChange={(value) => {
                setFormData({ ...formData, name: value });
                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              placeholder={t("faqCategory:placeholders.name")}
              error={errors.name}
              disabled={loading}
              required
              isRTL={isRTL}
            />
          </div>

          <div className="form-group full-width">
            <MultiLanguageTextInput
              label={t("faqCategory:fields.description")}
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder={t("faqCategory:placeholders.description")}
              disabled={loading}
              isRTL={isRTL}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="displayOrder">{t("faqCategory:fields.displayOrder")}</label>
            <input
              type="number"
              id="displayOrder"
              className="form-control"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) || 0 })}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t("faqCategory:fields.isActive")}</label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                disabled={loading}
              />
              <label className="form-check-label">
                {formData.isActive ? t("faqCategory:status.active") : t("faqCategory:status.inactive")}
              </label>
            </div>
          </div>
        </div>
      </form>
    </PageLayout>
  );
};
